import React, { useEffect, useState } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import ScienceBackground from '../../components/layout/ScienceBackground';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';


const CourseCard = ({ course, onSelect }) => (
  <div className="bg-white rounded-xl shadow-[0_2px_10px_rgb(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 flex flex-col overflow-hidden transition-shadow duration-300 cursor-pointer group">
    <div className={`w-full h-56 ${course.bg_pattern || 'bg-gradient-to-br from-blue-600 to-indigo-800'} relative flex flex-col items-center justify-center text-center p-4 overflow-hidden`}>
      <div className="absolute inset-0 bg-yellow-500/10 rotate-12 scale-150 transform transition-transform duration-700 group-hover:rotate-45"></div>
      <div className="z-10 space-y-2">
        <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-[10px] font-black text-white uppercase tracking-widest border border-white/10">
          {course.subject || 'Science'}
        </span>
        <h3 className="text-white font-sinhala font-black text-2xl drop-shadow-md leading-tight">
          {course.title}
        </h3>
      </div>
    </div>
    
    <div className="p-6 flex-grow flex flex-col font-sinhala">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 font-bold border border-slate-100">
          {course.teacher_name?.charAt(0) || 'T'}
        </div>
        <div>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-1">Assigned Teacher</p>
          <p className="font-black text-slate-700 text-sm uppercase">{course.teacher_name || 'Kasun Walakumbura'}</p>
        </div>
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <span className="inline-block px-3 py-1 bg-teal-50 text-teal-600 text-[10px] font-black rounded uppercase tracking-widest">
          {course.tag}
        </span>
        <div className="font-sans font-black text-slate-800 text-lg">
          LKR {course.price}
        </div>
      </div>
      
      <div className="mt-auto">
        <button 
          onClick={() => onSelect(course)}
          className="w-full py-3.5 bg-gradient-to-r from-orange-400 to-orange-600 text-white font-black rounded-2xl shadow-lg shadow-orange-100 hover:scale-[1.02] active:scale-95 transition-all text-sm uppercase tracking-widest"
        >
          View Months & Enroll
        </button>
      </div>
    </div>
  </div>
);

const Courses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter States
  const [selectedTeacher, setSelectedTeacher] = useState('ALL');
  const [selectedMedium, setSelectedMedium] = useState('ALL');

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchCourses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_courses.php`);
        const result = await response.json();
        if (result.status === 'success') {
          setCourses(result.data);
          setFilteredCourses(result.data);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Filter Logic
  useEffect(() => {
    let filtered = courses;
    
    if (selectedTeacher !== 'ALL') {
      filtered = filtered.filter(c => {
        const normalizedName = (c.teacher_name === 'Kasun Walakumbura' ? 'KASUN' : c.teacher_name).toUpperCase();
        return normalizedName === selectedTeacher;
      });
    }
    
    if (selectedMedium !== 'ALL') {
      filtered = filtered.filter(c => c.medium.toUpperCase() === selectedMedium);
    }
    
    setFilteredCourses(filtered);
  }, [selectedTeacher, selectedMedium, courses]);

  // Extract unique teachers and mediums
  const uniqueTeachers = [...new Set(courses.map(c => c.teacher_name).filter(Boolean))];
  const teacherMapping = uniqueTeachers.reduce((acc, name) => {
    const displayName = (name === 'Kasun Walakumbura' ? 'KASUN' : name).toUpperCase();
    if (!acc.includes(displayName)) {
      acc.push(displayName);
    }
    return acc;
  }, ['ALL']);
  
  const mediums = ['ALL', 'SINHALA', 'ENGLISH'];

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-800 overflow-x-hidden selection:bg-orange-500 selection:text-white relative flex flex-col pt-24">
      <ScienceBackground />
      <Navbar />

      <div className="relative z-10 pt-12 pb-10 px-6 text-center w-full max-w-4xl mx-auto">
        <div className="inline-block px-4 py-1.5 rounded-full bg-blue-100 text-blue-600 font-bold text-sm tracking-wide mb-4 border border-blue-200 shadow-sm">
          Explore Our Classes 🚀
        </div>
        <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-slate-800 mb-4 drop-shadow-sm font-sans">
          ALL <span className="text-blue-600">COURSES</span>
        </h1>
        <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto font-sinhala leading-relaxed">
          පහත දැක්වෙන ඕනෑම පන්තියක් සඳහා ඔබට මාසිකව ලියාපදිංචි වී ඉගෙනීම් කටයුතු සිදු කළ හැකිය.
        </p>
      </div>

      <div className="relative z-10 flex-grow px-6 md:px-12 pb-24 max-w-[1400px] mx-auto w-full flex flex-col gap-8">
        
        {/* Filter Toolbar */}
        {!loading && courses.length > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 bg-white/50 backdrop-blur-md p-8 rounded-[2.5rem] border border-white shadow-xl shadow-slate-200/40">
            {/* Teacher Filter */}
            <div className="flex flex-col gap-3 w-full md:w-auto min-w-[250px]">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Filter by Teacher</label>
              <div className="flex flex-wrap gap-2.5">
                {teacherMapping.map(display => (
                  <button
                    key={display}
                    onClick={() => setSelectedTeacher(display)}
                    className={`px-5 py-3 rounded-2xl text-sm font-black transition-all ${
                      selectedTeacher === display 
                        ? 'bg-slate-900 text-white shadow-xl shadow-slate-200' 
                        : 'bg-white text-slate-600 border border-slate-100 hover:bg-slate-50'
                    }`}
                  >
                    {display}
                  </button>
                ))}
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="hidden md:block w-px h-16 bg-slate-200 mx-6"></div>

            {/* Medium Filter */}
            <div className="flex flex-col gap-3 w-full md:w-auto min-w-[200px]">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Filter by Medium</label>
              <div className="flex flex-wrap gap-2.5">
                {mediums.map(m => (
                  <button
                    key={m}
                    onClick={() => setSelectedMedium(m)}
                    className={`px-5 py-3 rounded-2xl text-sm font-black transition-all ${
                      selectedMedium === m 
                        ? 'bg-orange-500 text-white shadow-xl shadow-orange-100' 
                        : 'bg-white text-slate-600 border border-slate-100 hover:bg-slate-50'
                    }`}
                  >
                    {m.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="w-full flex flex-col gap-6">
          
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {filteredCourses.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
                  {filteredCourses.map((course) => (
                    <CourseCard 
                      key={course.id} 
                      course={course} 
                      onSelect={(c) => navigate(`/course-details/${c.id}`)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-32 bg-white/40 rounded-[2.5rem] border border-dashed border-slate-200">
                  <h3 className="text-2xl font-bold text-slate-400">No courses match your selection.</h3>
                  <button 
                    onClick={() => { setSelectedTeacher('All'); setSelectedMedium('All'); }}
                    className="mt-4 text-blue-600 font-bold hover:underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Courses;
